import * as loadjs from 'loadjs'
import { LoadJsBundle } from '../enums/LoadJsBundle'
import { domReady } from '../helpers/DOM'
import isBelowViewport from '../helpers/DOM/isBelowViewport'
import Logger from '../helpers/Logger'
import type WebModel from '../models/WebModel'
import {
  optimizingForDesktopPagespeed,
  optimizingForMobilePagespeed
} from '../models/WebModel'
import type PrebidFacade from '../prebidFacade/PrebidFacade'
import type SlotFactory from '../slots/SlotFactory'
import SlotRepository from '../slots/SlotRepository'
import { TimeoutValue } from '../slots/helpers/Timeout'
import Adhesions from './Adhesions'
import type Blocklist from '../blocklist/Blocklist'
import UniversalPlayers from './UniversalPlayers'
import { queryParamHas } from '../helpers/getQueryParam'
import type { Slot } from '../slots/Slot'
import { Device } from '../helpers'

/**
 * ATF class
 * Responsible for displaying ATF slots
 */
export default class ATF {
  renderedPromise: Promise<void>
  constructor(
    readonly model: WebModel,
    readonly slotFactory: SlotFactory,
    readonly prebidFacade: PrebidFacade,
    readonly blocklist: Blocklist
  ) {
    this.renderedPromise = new Promise((resolve) => {
      domReady(() => {
        this.render().then(() => {
          resolve()
        })
      })
    })
  }

  async render(): Promise<void> {
    for (const slotModel of this.model.slots) {
      if (slotModel.lazy) {
        continue
      }

      if (shouldDisableSlotForPagespeed(slotModel)) {
        Logger.debug(
          `${slotModel.adUnitId} disabled for Desktop Pagespeed Optimization`
        )
        continue
      }

      await this.slotFactory.new(slotModel)
    }

    const universalPlayers = new UniversalPlayers(this.model, this.slotFactory)
    const adhesions = new Adhesions(this.model, this.slotFactory)

    await Promise.all([
      adhesions.renderedPromise,
      universalPlayers.renderedPromise
    ])

    let nonLazySlots = SlotRepository.all().filter((s) => !s.lazy)
    const allTimeouts = nonLazySlots.map((slot) => slot.timeout.value)
    const timeout = Math.max(...allTimeouts) as TimeoutValue

    nonLazySlots = handleOutstreamTest(nonLazySlots, this.prebidFacade)

    this.prebidFacade.requestBids(nonLazySlots, timeout).then(() => {
      loadjs.done(LoadJsBundle.nonLazyRequestsLoaded)
    })

    // Trigger after 5 seconds in case ATF ads take too long
    const fiveSecs = 5000
    setTimeout(() => {
      loadjs.done(LoadJsBundle.nonLazyRequestsLoaded)
    }, fiveSecs)
  }
}

/**
 * If ?test=outstream, we need to force the UniversalPlayer/MobileAdhesion to have a pbjs.requestBids()
 * that contains only one request.  This avoids a limitation in Pubmatic's Prebid BidAdapter where you can't
 * make multiple bid requests in the same network request with different publisherIds.
 * @param nonLazySlots
 */
function handleOutstreamTest(
  nonLazySlots: Slot[],
  prebidFacade: PrebidFacade
): Slot[] {
  if (queryParamHas('test', 'outstream')) {
    // Split nonLazySlots into soloSlots, which need to be requested individually;
    // and theRest, which can go out in a batch
    const { soloSlots, theRest } = splitNonLazySlotsForOutstreamTest(
      nonLazySlots
    )
    nonLazySlots = theRest

    // RequestBids for the slots in soloSlots one at a time.
    soloSlots.forEach((slot) => {
      prebidFacade.requestBids([slot], slot.timeout.value)
    })
  }

  return nonLazySlots
}

/**
 * Splits the nonLazySlots into two new arrays: soloSlots and theRest.
 * soloSlots contains slots that must have bidRequests go out one at a time.
 * theRest contains any ATF slot that can go out in a batched bidRequest.
 * @param nonLazySlots - Array of nonLazySlots
 */
function splitNonLazySlotsForOutstreamTest(nonLazySlots: Slot[]) {
  return nonLazySlots.reduce(
    (splitSlots, slot: Slot) => {
      if (
        slot.type === 'UniversalPlayer' ||
        (Device.isMobileOrTablet && slot.type === 'Adhesion')
      ) {
        splitSlots.soloSlots.push(slot)
      } else {
        splitSlots.theRest.push(slot)
      }
      return splitSlots
    },
    { soloSlots: [], theRest: [] } as { soloSlots: Slot[]; theRest: Slot[] }
  )
}

function shouldDisableSlotForPagespeed(slotModel: ISlotModel): boolean {
  return (
    (optimizingForDesktopPagespeed() || optimizingForMobilePagespeed()) &&
    !isBelowViewport(slotModel)
  )
}
